import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FeatureFlagsService, type FeatureFlag } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

/**
 * Global (system-wide) feature flags from the `feature_flags` table. Distinct
 * from `useAllFeatureFlags` in `useFeatureFlags.ts`, which reads per-user flags.
 */
export const useGlobalFeatureFlags = () =>
  useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => fetchAll<FeatureFlag>(FeatureFlagsService.getApiFeatureFlags),
    staleTime: 60_000,
  });

export const useUpdateFeatureFlag = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const res = await FeatureFlagsService.putApiFeatureFlagsById({
        path: { id },
        body: { enabled },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to update feature flag");
      return res.data as unknown as FeatureFlag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-flags"] });
    },
  });
};
